import React from 'react';

export type ClientStatus = 'inactive' | 'needs_attention' | 'at_risk' | 'excelling' | 'active_on_track';

interface StatusConfig {
  badge: string;
  color: string;
  icon: string;
  bgColor: string;
  borderColor: string;
}

interface StatusBadgeProps {
  status: ClientStatus;
  badge: string;
  color: string;
  icon: string;
  size?: 'small' | 'medium' | 'large';
  showIcon?: boolean;
  className?: string;
}

const STATUS_CONFIGS: Record<ClientStatus, StatusConfig> = {
  inactive: {
    badge: 'Inactive',
    color: '#ef4444', // red
    icon: '🔴',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    borderColor: 'rgba(239, 68, 68, 0.3)'
  },
  needs_attention: {
    badge: 'Needs Attention',
    color: '#eab308', // yellow
    icon: '🟡',
    bgColor: 'rgba(234, 179, 8, 0.1)',
    borderColor: 'rgba(234, 179, 8, 0.3)'
  },
  at_risk: {
    badge: 'At Risk',
    color: '#f97316', // orange
    icon: '🟠',
    bgColor: 'rgba(249, 115, 22, 0.1)',
    borderColor: 'rgba(249, 115, 22, 0.3)'
  },
  excelling: {
    badge: 'Excelling',
    color: '#10b981', // green
    icon: '🟢',
    bgColor: 'rgba(16, 185, 129, 0.1)',
    borderColor: 'rgba(16, 185, 129, 0.3)'
  },
  active_on_track: {
    badge: 'Active',
    color: '#3b82f6', // blue
    icon: '🔵',
    bgColor: 'rgba(59, 130, 246, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.3)'
  }
};

/**
 * StatusBadge Component
 * Phase 4: Reusable 5-tier client status badge
 *
 * Displays client engagement status with color-coded badge and icon.
 * Used in ActivityMetricsTooltip and potentially other locations.
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  badge,
  color: _color,
  icon,
  size = 'medium',
  showIcon = true,
  className = ''
}) => {
  // Get config for this status (fallback to active if status unknown)
  const config = STATUS_CONFIGS[status] || STATUS_CONFIGS.active_on_track;

  // Size-specific styles
  const sizeStyles = {
    small: {
      fontSize: '11px',
      padding: '4px 8px',
      gap: '4px'
    },
    medium: {
      fontSize: '12px',
      padding: '6px 10px',
      gap: '6px'
    },
    large: {
      fontSize: '14px',
      padding: '8px 12px',
      gap: '8px'
    }
  };

  const currentSize = sizeStyles[size];

  return (
    <span
      className={`status-badge ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: currentSize.gap,
        padding: currentSize.padding,
        backgroundColor: config.bgColor,
        border: `1px solid ${config.borderColor}`,
        borderRadius: '4px',
        fontSize: currentSize.fontSize,
        fontWeight: '600',
        color: config.color,
        whiteSpace: 'nowrap'
      }}
    >
      {showIcon && <span style={{ fontSize: currentSize.fontSize }}>{icon}</span>}
      <span>{badge}</span>
    </span>
  );
};

/**
 * Helper: Get status configuration by status key
 * Useful for external components that need status colors/icons
 */
export function getStatusConfig(status: ClientStatus): StatusConfig {
  return STATUS_CONFIGS[status] || STATUS_CONFIGS.active_on_track;
}
